// Industry-specific UI options for the tenant Users page (and anywhere else that
// needs industry-scoped user types, departments, or designations).
//
// Each industry defines:
//   userTypes:    { value, label }[]   — dropdown options for form.userType
//   departments:  string[]             — suggested department values
//   designations: { [userType]: string[] }  — designations shown when that userType is selected
//
// Values under `userType` MUST match the enum in backend/core/models/User.js.

const INDUSTRY_CONFIG = {
  education: {
    label: 'Education (School / College)',
    userTypes: [
      { value: 'teaching_staff', label: 'Teaching Staff (Teacher / Faculty)' },
      { value: 'non_teaching',   label: 'Non-Teaching Staff (Support, Helper)' },
      { value: 'admin_staff',    label: 'Administrative Staff (Accounts, HR, Office)' },
      { value: 'management',     label: 'Management (Principal, Vice Principal, HOD)' },
      { value: 'student',        label: 'Student' },
      { value: 'parent',         label: 'Parent / Guardian' },
      { value: 'other',          label: 'Other' },
    ],
    departments: [
      'Administration', 'Academics', 'Mathematics', 'Science', 'English / Language',
      'Social Studies', 'Arts & Physical Education', 'Computer Science', 'Commerce',
      'Library', 'Finance', 'Transport', 'Sports', 'Counselling', 'Support Staff', 'Other',
    ],
    designations: {
      management:     ['Principal', 'Vice Principal', 'Head of Department (HOD)', 'Academic Coordinator', 'Department Coordinator'],
      teaching_staff: ['Senior Teacher', 'Teacher', 'Assistant Teacher', 'Guest Faculty', 'Lab Instructor', 'Physical Education Teacher', 'Special Educator'],
      non_teaching:   ['Librarian', 'Lab Technician', 'Peon / Helper', 'Security Guard', 'Housekeeping Staff', 'Driver', 'Bus Attendant'],
      admin_staff:    ['Administrative Officer', 'Accounts Officer', 'Clerk', 'HR Manager', 'Receptionist', 'Data Entry Operator'],
      student:        ['Regular Student', 'Part-time Student'],
      parent:         ['Father', 'Mother', 'Guardian'],
      other:          ['Counselor', 'Nurse', 'Other'],
    },
  },

  training_institute: {
    label: 'Training Institute',
    userTypes: [
      { value: 'teaching_staff', label: 'Trainer / Faculty' },
      { value: 'admin_staff',    label: 'Admin / Office Staff' },
      { value: 'management',     label: 'Management (Director, Manager)' },
      { value: 'student',        label: 'Trainee / Student' },
      { value: 'other',          label: 'Other' },
    ],
    departments: [
      'Administration', 'Training', 'Placement', 'Accounts', 'HR', 'Marketing',
      'IT / Technical', 'Operations', 'Reception', 'Other',
    ],
    designations: {
      management:     ['Director', 'Manager', 'Centre Head', 'Operations Lead'],
      teaching_staff: ['Senior Trainer', 'Trainer', 'Assistant Trainer', 'Subject Matter Expert', 'Guest Lecturer'],
      admin_staff:    ['Admin Officer', 'Accounts Officer', 'Counselor', 'Placement Officer', 'Receptionist'],
      student:        ['Full-time Trainee', 'Part-time Trainee', 'Weekend Batch', 'Corporate Trainee'],
      other:          ['Intern', 'Mentor', 'Visiting Faculty', 'Other'],
    },
  },

  hospital: {
    label: 'Hospital / Healthcare',
    userTypes: [
      { value: 'doctor',         label: 'Doctor / Physician' },
      { value: 'nurse',          label: 'Nurse' },
      { value: 'lab_technician', label: 'Lab Technician' },
      { value: 'support_staff',  label: 'Support Staff' },
      { value: 'admin_staff',    label: 'Admin / Office Staff' },
      { value: 'management',     label: 'Management' },
      { value: 'patient',        label: 'Patient' },
      { value: 'other',          label: 'Other' },
    ],
    departments: [
      'Administration', 'Cardiology', 'Orthopedics', 'Pediatrics', 'Gynecology',
      'Neurology', 'General Medicine', 'Surgery', 'Radiology', 'Pathology',
      'Pharmacy', 'Nursing', 'Billing', 'Reception', 'Housekeeping', 'Other',
    ],
    designations: {
      doctor:         ['Consultant', 'Senior Consultant', 'Resident', 'Specialist', 'Surgeon', 'Chief Medical Officer'],
      nurse:          ['Head Nurse', 'Staff Nurse', 'Nurse Assistant', 'ICU Nurse', 'OT Nurse'],
      lab_technician: ['Senior Lab Technician', 'Lab Technician', 'Radiology Technician', 'Pharmacist'],
      support_staff:  ['Ward Boy', 'House Keeping', 'Security Guard', 'Ambulance Driver'],
      admin_staff:    ['Administrator', 'Receptionist', 'Billing Officer', 'HR Officer', 'Accounts Officer'],
      management:     ['Hospital Director', 'Medical Superintendent', 'General Manager', 'Operations Head'],
      patient:        ['Inpatient', 'Outpatient', 'Emergency'],
      other:          ['Volunteer', 'Visiting Consultant', 'Other'],
    },
  },

  software_company: {
    label: 'Software / IT Company',
    userTypes: [
      { value: 'developer',   label: 'Developer / Engineer' },
      { value: 'tester',      label: 'QA / Tester' },
      { value: 'designer',    label: 'Designer (UI/UX / Graphic)' },
      { value: 'management',  label: 'Management' },
      { value: 'admin_staff', label: 'Admin / HR Staff' },
      { value: 'client',      label: 'Client' },
      { value: 'other',       label: 'Other' },
    ],
    departments: [
      'Engineering', 'Product', 'Design', 'QA', 'DevOps', 'Marketing', 'Sales',
      'Customer Support', 'HR', 'Finance', 'Administration', 'IT Support', 'Other',
    ],
    designations: {
      developer:   ['Software Engineer', 'Senior Engineer', 'Tech Lead', 'Architect', 'Engineering Manager', 'Intern', 'Trainee'],
      tester:      ['QA Engineer', 'Senior QA', 'QA Lead', 'SDET', 'Manual Tester', 'Automation Engineer'],
      designer:    ['UI Designer', 'UX Designer', 'Product Designer', 'Graphic Designer', 'Design Lead'],
      management:  ['CEO', 'CTO', 'Director', 'VP', 'Project Manager', 'Product Manager', 'Team Lead'],
      admin_staff: ['HR Executive', 'Recruiter', 'Office Manager', 'Accountant', 'Admin'],
      client:      ['Primary Contact', 'Stakeholder', 'Sponsor'],
      other:       ['Consultant', 'Freelancer', 'Intern', 'Other'],
    },
  },

  retail: {
    label: 'Retail / Store',
    userTypes: [
      { value: 'cashier',       label: 'Cashier' },
      { value: 'sales_staff',   label: 'Sales Staff' },
      { value: 'store_manager', label: 'Store Manager' },
      { value: 'management',    label: 'Management' },
      { value: 'admin_staff',   label: 'Admin / Office Staff' },
      { value: 'support_staff', label: 'Support Staff (Housekeeping, Security)' },
      { value: 'customer',      label: 'Customer' },
      { value: 'supplier',      label: 'Supplier / Vendor' },
      { value: 'other',         label: 'Other' },
    ],
    departments: [
      'Store Operations', 'Sales', 'Billing / Cashier', 'Inventory / Warehouse',
      'Procurement', 'Purchase', 'Marketing', 'Customer Service', 'HR',
      'Accounts', 'Security', 'Housekeeping', 'Other',
    ],
    designations: {
      cashier:       ['Senior Cashier', 'Cashier', 'Billing Assistant'],
      sales_staff:   ['Senior Sales Executive', 'Sales Executive', 'Sales Associate', 'Floor Staff', 'Customer Assistant'],
      store_manager: ['Store Manager', 'Assistant Store Manager', 'Shift Manager'],
      management:    ['Regional Manager', 'Area Manager', 'Operations Head', 'Director'],
      admin_staff:   ['HR Officer', 'Accounts Officer', 'Admin', 'Receptionist'],
      support_staff: ['Housekeeping', 'Security Guard', 'Loader / Helper', 'Driver'],
      customer:      ['Walk-in', 'Regular', 'Loyalty Member', 'VIP'],
      supplier:      ['Primary Vendor', 'Secondary Vendor', 'Logistics Partner'],
      other:         ['Intern', 'Contractor', 'Other'],
    },
  },

  manufacturing: {
    label: 'Manufacturing / Factory',
    userTypes: [
      { value: 'operator',      label: 'Machine Operator / Worker' },
      { value: 'supervisor',    label: 'Supervisor / Shift Lead' },
      { value: 'engineer',      label: 'Engineer' },
      { value: 'management',    label: 'Management' },
      { value: 'admin_staff',   label: 'Admin / Office Staff' },
      { value: 'support_staff', label: 'Support Staff' },
      { value: 'vendor',        label: 'Vendor / Supplier' },
      { value: 'other',         label: 'Other' },
    ],
    departments: [
      'Production', 'Quality Control', 'Maintenance', 'Warehouse',
      'Procurement', 'Logistics', 'R&D', 'Safety', 'HR', 'Accounts',
      'Administration', 'Security', 'Other',
    ],
    designations: {
      operator:      ['Machine Operator', 'Assembly Line Worker', 'Helper', 'Packer', 'Quality Checker'],
      supervisor:    ['Shift Supervisor', 'Floor Supervisor', 'Production Supervisor', 'Quality Supervisor'],
      engineer:      ['Production Engineer', 'Mechanical Engineer', 'Electrical Engineer', 'Quality Engineer', 'Maintenance Engineer'],
      management:    ['Plant Manager', 'Production Head', 'Factory Director', 'Operations Head'],
      admin_staff:   ['HR Officer', 'Accounts Officer', 'Admin', 'Safety Officer'],
      support_staff: ['Security Guard', 'Housekeeping', 'Canteen Staff', 'Driver'],
      vendor:        ['Raw Material Supplier', 'Equipment Supplier', 'Logistics Partner'],
      other:         ['Intern', 'Contractor', 'Consultant', 'Other'],
    },
  },

  corporate: {
    label: 'Corporate Office',
    userTypes: [
      { value: 'employee',      label: 'Employee' },
      { value: 'management',    label: 'Management' },
      { value: 'admin_staff',   label: 'Admin / HR Staff' },
      { value: 'support_staff', label: 'Support Staff' },
      { value: 'client',        label: 'Client' },
      { value: 'vendor',        label: 'Vendor' },
      { value: 'other',         label: 'Other' },
    ],
    departments: [
      'Administration', 'HR', 'Finance', 'Sales', 'Marketing', 'Operations',
      'IT', 'Legal', 'Procurement', 'Customer Service', 'Research', 'Other',
    ],
    designations: {
      employee:      ['Executive', 'Senior Executive', 'Assistant Manager', 'Manager', 'Senior Manager', 'Associate'],
      management:    ['CEO', 'CFO', 'COO', 'Director', 'Vice President', 'General Manager'],
      admin_staff:   ['HR Executive', 'Recruiter', 'Accountant', 'Office Administrator', 'Receptionist'],
      support_staff: ['Security', 'Housekeeping', 'Driver', 'Pantry Staff'],
      client:        ['Primary Contact', 'Stakeholder', 'Sponsor'],
      vendor:        ['Service Provider', 'Supplier', 'Logistics Partner'],
      other:         ['Intern', 'Consultant', 'Contractor', 'Other'],
    },
  },
};

// Generic fallback used when industry is unknown or not in the map.
const DEFAULT_CONFIG = INDUSTRY_CONFIG.corporate;

/**
 * Returns the config block for a given industry slug.
 * Falls back to the corporate/generic config if the slug is unknown.
 */
export function getIndustryConfig(slug) {
  if (!slug) return DEFAULT_CONFIG;
  return INDUSTRY_CONFIG[slug] || DEFAULT_CONFIG;
}

/**
 * Returns a flat map of userType value → label for quickly labelling a user row.
 */
export function getUserTypeLabels(slug) {
  const cfg = getIndustryConfig(slug);
  const map = {};
  cfg.userTypes.forEach(t => { map[t.value] = t.label; });
  return map;
}

export { INDUSTRY_CONFIG };
